// Hub manifest types: what a hub answers and how its parameters resolve, as
// plain data. A toolset carries its manifest as manifest.json, which the
// StagingWorkflow stores in its toolsets row. Nothing here assumes a
// particular extractor: the router model, span extractors, GLiNER, and
// fixed-label NER models each get an adapter that renders these declarations
// into their own input. See Design > Toolsets and tools, "Hub manifest".

import { Result, Schema, SchemaIssue } from "effect";
import { shapesToCheck, type ShapedAnswer } from "./answer.ts";
import { manifestShapeDefOf } from "./defs.ts";
import { shapeFieldProblems } from "./field-format.ts";
import { JsonObject } from "./json.ts";
import { isShapeName } from "./schema.ts";
import {
  ACCOUNT_NAME, API_ACCOUNTS_PER_HOST, DEFAULT_DISPATCH_THRESHOLD, DEFAULT_EGRESS_ALLOW, DISPATCH_THRESHOLD_FLOOR, MAX_BUNDLE_BYTES, MAX_EXAMPLES_PER_INTENT, MAX_GOLDEN_QUESTIONS,
  MAX_INTENTS, MAX_MANIFEST_BYTES, MAX_TOOLSET_BUNDLE_BYTES,
} from "./limits.ts";

// ---------------------------------------------------------------- entities

/**
 * Matches a value against a name field, such as officialName. `case` says how
 * the field is stored, because WarmHub prefix queries are case-sensitive.
 */
const NameMatch = Schema.Struct({
  field: Schema.String,
  shapes: Schema.optionalKey(Schema.Array(Schema.String)),
  case: Schema.optionalKey(Schema.Literals(["as-written", "upper", "title"])),
});

/**
 * Matches a value through another shape and a shared field: "NC" finds the
 * CensusState whose stateAbbreviation is NC, then the State with its geoid.
 */
const JoinMatch = Schema.Struct({
  via: Schema.Struct({
    shape: Schema.String,
    field: Schema.String,
    case: Schema.optionalKey(Schema.Literals(["as-written", "upper", "title"])),
    join: Schema.Struct({ from: Schema.String, to: Schema.String }),
  }),
  shapes: Schema.Array(Schema.String),
});

/**
 * A recognizable code, such as a county GEOID. `pattern` is a regular
 * expression source without anchors; when it has a capture group, the group
 * is the code. Code patterns validate extracted values, and they scan the
 * question for hints before extraction.
 */
const CodePattern = Schema.Struct({
  label: Schema.String,
  pattern: Schema.String,
  field: Schema.String,
  shapes: Schema.Array(Schema.String),
});

/**
 * A pattern that suggests the kind of entity without identifying one, such
 * as "<Name> County". Markers produce hints only.
 */
const Marker = Schema.Struct({
  label: Schema.String,
  pattern: Schema.String,
  shapes: Schema.optionalKey(Schema.Array(Schema.String)),
});

/**
 * Narrows candidates by another resolved parameter. `from` names the value
 * on that parameter: "value" for its text or choice, "id" for its Thing's
 * durable ID, or a data path on its Thing, such as "geoid" or
 * "sourceRecord.stateAbbreviation" (which follows the reference).
 */
const NarrowRule = Schema.Struct({
  param: Schema.String,
  shapes: Schema.optionalKey(Schema.Array(Schema.String)),
  field: Schema.String,
  op: Schema.Literals(["eq", "prefix", "contains"]),
  from: Schema.String,
  case: Schema.optionalKey(Schema.Literals(["as-written", "upper", "title"])),
});

/**
 * Decodes an entity declaration: what a parameter value resolves to, and how.
 * Its `repo` must be one of the hub's readable repositories, and each code
 * pattern's shapes must be among its `shapes` (checkManifest).
 */
export const EntitySpec = Schema.Struct({
  /** Short type label that MissingParameters messages name, such as "county". */
  label: Schema.String,
  /** What the entity is, for people who read the manifest. The Exchange doesn't read it. */
  description: Schema.String,
  /** The repository the entity's Things are in: one of the hub's readable repositories. */
  repo: Schema.String,
  /** The shapes a value may resolve to, by name. */
  shapes: Schema.Array(Schema.String),
  /** How a value matches a Thing: by a name field, or through another shape. */
  match: Schema.Array(Schema.Union([NameMatch, JoinMatch])),
  codes: Schema.optionalKey(Schema.Array(CodePattern)),
  markers: Schema.optionalKey(Schema.Array(Marker)),
  narrowBy: Schema.optionalKey(Schema.Array(NarrowRule)),
  /** Picks shapes from a choice parameter, such as geographyKind = county. */
  shapeFrom: Schema.optionalKey(Schema.Struct({ param: Schema.String, map: Schema.Record(Schema.String, Schema.Array(Schema.String)) })),
  /** Breaks ties: prefer candidates whose field has this value. */
  prefer: Schema.optionalKey(Schema.Array(Schema.Struct({ field: Schema.String, equals: Schema.String }))),
  /** Words that refer to this kind of entity without naming one. */
  nouns: Schema.optionalKey(Schema.Array(Schema.String)),
  /** Fields that tell matches apart in a MissingParameters message. */
  detail: Schema.optionalKey(Schema.Array(Schema.String)),
  /**
   * Labels that fixed-label NER models use for this kind, such as LOC or ORG.
   * Only the NER adapter reads them, and no path serves it. See Design >
   * HubRouter, "Extractor adapters".
   */
  nerTypes: Schema.optionalKey(Schema.Array(Schema.String)),
  /** Seconds to cache validation lookups in KV. */
  cacheTtl: Schema.optionalKey(Schema.Finite),
  /**
   * Words that don't tell this entity's names apart, such as "county" or
   * "national". Name scoring ignores them, as it ignores English function
   * words.
   */
  ignoreWords: Schema.optionalKey(Schema.Array(Schema.String)),
  /**
   * A pattern for a suffix that Thing names carry and values usually leave
   * out, such as ", NC" in "Raleigh-Cary, NC". Name scoring compares exact
   * and contained names without it.
   */
  nameSuffix: Schema.optionalKey(Schema.String),
});
/** An entity declaration, for entity() and a manifest's `entities`: what a parameter value resolves to, and how. */
export type EntitySpec = typeof EntitySpec.Type;
/** A `match` entry that compares a value with a name field. WarmHub prefix queries are case-sensitive, so `case` says how the field is stored. */
export type NameMatch = typeof NameMatch.Type;
/** A `match` entry that finds a Thing through another shape and a shared field. */
export type JoinMatch = typeof JoinMatch.Type;
/** A recognizable code, such as a county GEOID, as an anchorless pattern; its capture group, when it has one, is the code. */
export type CodePattern = typeof CodePattern.Type;
/** A rule that narrows an entity's candidates by another resolved parameter. */
export type NarrowRule = typeof NarrowRule.Type;

// -------------------------------------------------------------- parameters

const ParamBase = {
  name: Schema.String,
  /** Short span label, such as "county name", unique within the intent. */
  label: Schema.String,
  description: Schema.String,
  required: Schema.Boolean,
  /**
   * What a span extractor is told to find for this parameter, in place of
   * its label and description, such as "The area whose total area the
   * percentage is taken of, by its name only". See Design > HubRouter,
   * "Digital twins".
   */
  extractorInstructions: Schema.optionalKey(Schema.String),
  /**
   * A regular expression source, without anchors, that a span extractor's
   * value must match whole, as written, such as a permit number's format. A
   * value that doesn't match is dropped, and a guess that matches stands in
   * for an empty value. Without one, a text parameter's `pattern` serves,
   * ignoring case as resolution does.
   */
  format: Schema.optionalKey(Schema.String),
};

/** Decodes a parameter declaration, one of the three kinds below. Declare one with param. */
export const ParamSpec = Schema.Union([
  /** A value that resolves to one WarmHub Thing of the named entity. */
  Schema.Struct({ ...ParamBase, kind: Schema.Literal("entity"), entity: Schema.String }),
  /**
   * One of a fixed set of values. Each value lists synonyms, so an extracted
   * span such as "square miles" normalizes to "mi2".
   */
  Schema.Struct({ ...ParamBase, kind: Schema.Literal("choice"), choices: Schema.Record(Schema.String, Schema.Array(Schema.String)) }),
  /**
   * Text the tool reads as written, optionally checked against a pattern.
   * The platform trims a padded or extended code back to what the question
   * wrote, and otherwise passes the router model's value through.
   * `codesFrom` names entities whose code patterns could fill it from hints;
   * only shadow mode reads it, to record whether the fast path could have
   * skipped intent routing. See Design > HubRouter, "Shadow mode".
   */
  Schema.Struct({
    ...ParamBase,
    kind: Schema.Literal("text"),
    pattern: Schema.optionalKey(Schema.String),
    codesFrom: Schema.optionalKey(Schema.Array(Schema.String)),
  }),
]);
/** An intent's parameter: an entity, a choice or a text parameter. Declare one with param. */
export type ParamSpec = typeof ParamSpec.Type;

// ------------------------------------------------------------------ shapes

/**
 * Decodes a manifest shape: an answer shape the toolset defines, in WarmHub's
 * shape format, which answers name by its name alone, such as "Quote".
 * checkManifest checks its name and fields. See Design > Answer shapes,
 * "Manifest shapes".
 */
export const ManifestShape = Schema.Struct({
  description: Schema.optionalKey(Schema.String),
  /** Its fields, in WarmHub's shape field format. */
  fields: JsonObject,
});
/** A manifest shape: an answer shape the toolset defines, in WarmHub's shape format. */
export type ManifestShape = typeof ManifestShape.Type;

// ----------------------------------------------------------------- intents

/** Decodes an intent declaration. Declare one with intent(). */
export const IntentSpec = Schema.Struct({
  /** The intent's ID, unique in the manifest, which `tools/<id>.js` and golden questions name. */
  id: Schema.String,
  /** Short class label for intent classifiers. */
  label: Schema.String,
  /** Longer description for tool-calling models. */
  description: Schema.String,
  /** The intent's ask, in US dollars per million answers. A bid below it gets PriceTooLow. */
  askUsdPerMillion: Schema.Finite,
  /**
   * Allowed shapes for the answer's root and collection members, as
   * "org/repo/Name", "system:Set", or a manifest shape's name (Design >
   * Answer shapes, rules 3 and 6).
   */
  answer: Schema.Struct({ rootShapes: Schema.Array(Schema.String), memberShapes: Schema.optionalKey(Schema.Array(Schema.String)) }),
  params: Schema.Array(ParamSpec),
  examples: Schema.Array(Schema.String),
  /**
   * The tool that answers this intent, or null when none is built yet: its
   * module under src/, and optionally its export after "#", such as
   * "tools/example.ts#example". Without "#", the module's default export.
   * Only `npm run build` reads the module; the Exchange reads only whether
   * it's null, and dispatch runs the bundle that staging recorded.
   */
  tool: Schema.NullOr(Schema.String),
  /**
   * A pure lookup whose required parameters a verified code can fill. Shadow
   * mode records when such an intent could have skipped intent routing, and
   * the fast-path twin records the flag. It doesn't decide where the fast path
   * serves: `FAST_PATH` does. See Design > HubRouter, "Shadow mode".
   */
  fastPath: Schema.optionalKey(Schema.Boolean),
  /**
   * Two parameters whose values a question names as a pair, such as the area
   * measured and the area it lies in. A span extractor's two values fill
   * `first` then `second` in the order the question names them, except that
   * when exactly one value follows `cue`, that value fills `first`. `cue` is
   * a regular expression source matched, ignoring case, against the
   * question's text before the value, such as "\\bof\\s+(?:the\\s+)?$". See Design > HubRouter,
   * "Digital twins".
   */
  orderedPair: Schema.optionalKey(Schema.Struct({ first: Schema.String, second: Schema.String, cue: Schema.optionalKey(Schema.String) })),
});
/** An intent: a kind of question the hub answers, its parameters, its answer shapes, its ask and its tool. */
export type IntentSpec = typeof IntentSpec.Type;

/**
 * Decodes a golden question: what the hub expects for it. `expectIntent` is
 * null for a question the hub expects the exchange to decline. See Design >
 * Toolsets and tools, "Golden question sets".
 */
export const GoldenQuestion = Schema.Struct({
  question: Schema.String,
  /** The intent the question should route to, or null for one the hub expects the exchange to decline. checkManifest reports an intent the manifest lacks. */
  expectIntent: Schema.NullOr(Schema.String),
  /** Answer, MissingParameters, NotFound, OutOfScope, … */
  expectOutcome: Schema.String,
  note: Schema.optionalKey(Schema.String),
});
/** A golden question: a question, and the intent and outcome the hub expects for it. */
export type GoldenQuestion = typeof GoldenQuestion.Type;

/**
 * Decodes a hub manifest: what a hub answers and how its parameters resolve.
 * A toolset carries it as manifest.json, which `npm run build` writes from
 * src/manifest.ts, and staging decodes it with this and checks it with
 * checkManifest. Declare one with hub().
 */
export const HubManifest = Schema.Struct({
  /** The hub's linked repository, such as "warmhub-data/us.geography", which must name the hub that owns the toolset repository. */
  hub: Schema.String,
  /**
   * A label the build writes as declared, such as "template". When the
   * Exchange decodes a toolset's row, it replaces this with the toolset's
   * commit SHA, which is what Curator Studio reads.
   */
  version: Schema.String,
  /** The other repositories the hub's tools may read, besides its linked repository. */
  reads: Schema.Array(Schema.String),
  scope: Schema.String,
  /** Data the hub doesn't hold, for the router model's prompt. */
  notInScope: Schema.String,
  /**
   * The lowest hub score at which the hub gets a question. Defaults to
   * DEFAULT_DISPATCH_THRESHOLD; can't go below DISPATCH_THRESHOLD_FLOOR.
   */
  dispatchThreshold: Schema.optionalKey(Schema.Finite),
  /**
   * The field to show as a Thing's title, for shapes in the hub's linked
   * repository that no entity names: shape name -> field. An entity's own
   * shapes use its name match field.
   */
  titles: Schema.optionalKey(Schema.Record(Schema.String, Schema.String)),
  /**
   * The domains the hub's tools can fetch from, through the EgressGateway.
   * Without it, the list is DEFAULT_EGRESS_ALLOW; with it, exactly these.
   * Each must be a hostname, not an IP address. `accounts` names, for a
   * host in `allow`, the accounts its tools may pick with fetchJson's
   * `account`, such as one per member of a family hub: at most
   * API_ACCOUNTS_PER_HOST, each matching ACCOUNT_NAME, never "default",
   * which every host has. See Design > Names and definitions, "Account".
   */
  egress: Schema.optionalKey(Schema.Struct({
    allow: Schema.Array(Schema.String),
    accounts: Schema.optionalKey(Schema.Record(Schema.String, Schema.Array(Schema.String))),
  })),
  entities: Schema.Record(Schema.String, EntitySpec),
  /** The answer shapes the toolset defines, by name. See Design > Answer shapes, "Manifest shapes". */
  shapes: Schema.optionalKey(Schema.Record(Schema.String, ManifestShape)),
  intents: Schema.Array(IntentSpec),
  golden: Schema.optionalKey(Schema.Array(GoldenQuestion)),
});
/** A hub manifest: what a hub answers and how its parameters resolve. Declare one with hub(). */
export type HubManifest = typeof HubManifest.Type;

// A failed decode as one line: each issue's message, after its path when it
// has one, such as a field of the wrong type, or a problem checkManifest found.
const formatIssue = SchemaIssue.makeFormatterStandardSchemaV1();
const oneLine = (e: Schema.SchemaError) =>
  formatIssue(e.issue).issues.map((i) => (i.path && i.path.length > 0 ? `${i.path.map(String).join(".")}: ${i.message}` : i.message)).join("; ");

const decodeManifestJson = Schema.decodeUnknownResult(Schema.fromJsonString(HubManifest));
/**
 * A manifest as JSON text, such as a toolset's manifest.json or its row's
 * manifest column, decoded: the manifest, or why it isn't one, on one line,
 * with the path where it failed. It doesn't run checkManifest: use it to show
 * a toolset whatever its state, and parseValidManifestJson to use one.
 */
export const parseManifestJson = (text: string): Result.Result<HubManifest, string> => Result.mapError(decodeManifestJson(text), oneLine);

/** The domains the hub's tools can fetch from, with the default applied. A new array each call. */
export const egressAllowOf = (m: HubManifest): string[] => [...(m.egress?.allow ?? DEFAULT_EGRESS_ALLOW)];

/**
 * The accounts the hub's tools may pick, each as `host/account`, sorted. A
 * new array each call; empty when the manifest declares none.
 */
export const egressAccountsOf = (m: HubManifest): string[] =>
  Object.entries(m.egress?.accounts ?? {}).flatMap(([host, names]) => names.map((name) => `${host}/${name}`)).sort();

/**
 * The hub's readable repositories: its linked repository, then its `reads`,
 * once each. Reads that name a repository outside these are refused.
 */
export const readableRepositoriesOf = (m: HubManifest): string[] => [...new Set([m.hub, ...m.reads])];

/**
 * Where the definition of each shape an answer's assertions and local Things
 * use comes from, as the HubRouter takes them: each manifest shape's from
 * the manifest, as written (`carried`, which an Answer carries) and
 * normalized (`defs`), and each pinned ref's from WarmHub (`pinned`, which
 * the caller reads its own way). A name the manifest doesn't define is in
 * neither, so rule 5 reports it. The HubRouter, the hub agent's lanes and the
 * recipe lab all call it, so they can't differ. See Design > Answer shapes,
 * "Manifest shapes".
 */
export function shapeSourcesOf(m: Pick<HubManifest, "shapes">, answer: ShapedAnswer) {
  const declared = m.shapes ?? {};
  const shapes = shapesToCheck(answer);
  const carried = Object.fromEntries(shapes.filter(isShapeName).flatMap((name) => {
    const shape = Object.hasOwn(declared, name) ? declared[name] : undefined;
    return shape ? [[name, shape] as const] : [];
  }));
  const defs = Object.fromEntries(Object.entries(carried).map(([name, shape]) => [name, manifestShapeDefOf(name, shape)]));
  return { carried, defs, pinned: shapes.filter((ref) => !isShapeName(ref)) };
}

/** The hub's dispatch threshold, with the default applied. It doesn't check the floor; checkManifest does. */
export const dispatchThresholdOf = (m: HubManifest) => m.dispatchThreshold ?? DEFAULT_DISPATCH_THRESHOLD;

// A hostname: labels of letters, digits and hyphens, at least two of them,
// the last with a letter, so an IP address such as 169.254.169.254 isn't one.
const HOSTNAME = /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*\.(?=[a-z0-9-]*[a-z])[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;

/**
 * Checks everything about a manifest that doesn't need the toolset's other
 * files: staging's checks 2 and 11 to 14, in the words staging rejects a
 * toolset with. It's what `npm run build` and staging both call, so a
 * mistake of these kinds shows before the push. Returns the problems of
 * `checkManifestIntegrity`, then each answer shape that is neither in the
 * hub's readable repositories nor a manifest shape, and each manifest shape
 * whose name or fields WarmHub's format doesn't allow (11), each egress
 * allowlist entry that isn't a
 * hostname (12), a missing golden question set (13), and a count over its
 * staging cap: intents, an intent's examples, or golden questions (14).
 * Empty means valid. It never throws.
 */
export function checkManifest(m: HubManifest): string[] {
  const problems = checkManifestIntegrity(m);
  const readable = readableRepositoriesOf(m);
  const shapes = m.shapes ?? {};
  for (const [name, shape] of Object.entries(shapes)) {
    if (!isShapeName(name)) problems.push(`shapes: ${name} isn't a shape name: a letter, then letters, digits or underscores`);
    for (const p of shapeFieldProblems(shape.fields)) problems.push(`shapes: ${name}: ${p}`);
  }
  for (const i of m.intents) {
    for (const ref of [...i.answer.rootShapes, ...(i.answer.memberShapes ?? [])]) {
      if (ref.startsWith("system:")) continue;
      if (isShapeName(ref)) {
        if (!Object.hasOwn(shapes, ref)) problems.push(`${i.id}: answer shape ${ref} isn't one of the manifest's shapes`);
        continue;
      }
      const [org, repo] = ref.split("/");
      if (!readable.includes(`${org}/${repo}`)) problems.push(`${i.id}: answer shape ${ref} isn't in one of the hub's readable repositories`);
    }
  }
  for (const entry of m.egress?.allow ?? []) {
    if (!HOSTNAME.test(entry)) problems.push(`egress.allow: ${entry} isn't a hostname`);
  }
  for (const [host, names] of Object.entries(m.egress?.accounts ?? {})) {
    if (!(m.egress?.allow ?? []).includes(host)) problems.push(`egress.accounts: ${host} isn't in egress.allow`);
    if (names.length > API_ACCOUNTS_PER_HOST) problems.push(`egress.accounts: ${host} has ${names.length} accounts; the limit is ${API_ACCOUNTS_PER_HOST}`);
    if (new Set(names).size !== names.length) problems.push(`egress.accounts: ${host} names an account twice`);
    for (const name of names) {
      if (name === "default") problems.push(`egress.accounts: ${host} declares "default", which every host has`);
      else if (!ACCOUNT_NAME.test(name)) problems.push(`egress.accounts: ${host}'s ${name} isn't an account name: lowercase letters, digits, _ and -, starting with a letter or digit`);
    }
  }
  const golden = m.golden?.length ?? 0;
  if (golden === 0) problems.push("the toolset has no golden questions");
  if (m.intents.length > MAX_INTENTS) problems.push(`the toolset has ${m.intents.length} intents; the limit is ${MAX_INTENTS}`);
  for (const i of m.intents) {
    if (i.examples.length > MAX_EXAMPLES_PER_INTENT) problems.push(`${i.id}: ${i.examples.length} example questions; the limit is ${MAX_EXAMPLES_PER_INTENT}`);
  }
  if (golden > MAX_GOLDEN_QUESTIONS) problems.push(`the toolset has ${golden} golden questions; the limit is ${MAX_GOLDEN_QUESTIONS}`);
  return problems;
}

/** A manifest as the manifest.json text the build writes, which staging reads and measures. */
export const manifestJsonOf = (m: HubManifest): string => `${JSON.stringify(m, null, 2)}\n`;

/** A text's size as staging counts a file's: its UTF-8 bytes. */
export const utf8Bytes = (text: string): number => new TextEncoder().encode(text).byteLength;

const KIB = 1024;
const MIB = 1024 * KIB;
// A size over its limit, rounded up, so one just over never prints as the limit.
const over = (bytes: number, unit: number, name: string) => `${(Math.ceil((bytes / unit) * 10) / 10).toFixed(1)} ${name}`;

/**
 * Checks a toolset's sizes, in UTF-8 bytes, against its staging caps:
 * manifest.json (15), each built intent's bundle (6), and its bundles
 * together (16). `manifestBytes` is the manifest.json that is pushed, not a
 * re-serialization. The build and staging both call it, so a toolset too
 * large to stage fails `npm run build` with the same problem text. Empty
 * means within every cap. See Design > Toolsets and tools, "Staging checks".
 */
export function checkToolsetBytes(manifestBytes: number, bundles: readonly { intent: string; size: number }[]): string[] {
  const problems: string[] = [];
  if (manifestBytes > MAX_MANIFEST_BYTES) problems.push(`manifest.json is ${over(manifestBytes, KIB, "KiB")}; the limit is ${MAX_MANIFEST_BYTES / KIB} KiB`);
  for (const b of bundles) {
    if (b.size > MAX_BUNDLE_BYTES) problems.push(`${b.intent}: bundle is ${over(b.size, MIB, "MiB")}; the limit is ${MAX_BUNDLE_BYTES / MIB} MiB`);
  }
  const total = bundles.reduce((sum, b) => sum + b.size, 0);
  if (total > MAX_TOOLSET_BUNDLE_BYTES) problems.push(`the bundles total ${over(total, MIB, "MiB")}; the limit is ${MAX_TOOLSET_BUNDLE_BYTES / MIB} MiB`);
  return problems;
}

/**
 * The questions an examples run asks: each built intent's first example
 * that the golden set doesn't expect to come back as anything but an
 * Answer, so a known gap doesn't fail the examples. See Design > Exchange,
 * "Test runs of a toolset".
 */
export function examplesToAsk(m: HubManifest): { intent: string; question: string }[] {
  const failing = new Set((m.golden ?? []).filter((g) => g.expectOutcome !== "Answer").map((g) => g.question));
  return m.intents.filter((i) => i.tool !== null).flatMap((i) => {
    const question = i.examples.find((e) => !failing.has(e));
    return question === undefined ? [] : [{ intent: i.id, question }];
  });
}

/**
 * Checks a manifest's dispatch threshold and internal references: every
 * entity parameter names a declared entity, every entity's repository is one
 * of the hub's readable repositories, every code's shapes are among its
 * entity's shapes, every pattern compiles, each intent's parameter labels are
 * unique, because span extractors map labels back to parameters, and every
 * golden question's intent is declared. Returns a list of problems, in the
 * words staging rejects a toolset with; empty means valid. It never throws.
 * ValidHubManifest runs it as it decodes, so a reader decodes that instead.
 * It leaves out the checks of `checkManifest` that are about publishing a
 * toolset, so a manifest staged before they existed still decodes where a
 * hub answers with it.
 */
export function checkManifestIntegrity(m: HubManifest): string[] {
  const problems: string[] = [];
  const t = m.dispatchThreshold;
  if (t !== undefined && !(t >= DISPATCH_THRESHOLD_FLOOR && t <= 1)) {
    problems.push(`dispatchThreshold ${t} must be from ${DISPATCH_THRESHOLD_FLOOR} to 1`);
  }
  const compiles = (where: string, source: string) => {
    const compiled = Result.try(() => new RegExp(source));
    if (Result.isFailure(compiled)) problems.push(`${where}: pattern ${source} doesn't compile: ${String(compiled.failure)}`);
  };
  // Parameter resolution and pattern hints read each entity's repository for
  // the hub, so it must be one of the hub's readable repositories.
  const readable = readableRepositoriesOf(m);
  for (const [name, e] of Object.entries(m.entities)) {
    if (!readable.includes(e.repo)) problems.push(`entity ${name}: ${e.repo} isn't one of the hub's readable repositories`);
    for (const c of e.codes ?? []) compiles(`entity ${name} code ${c.label}`, c.pattern);
    for (const k of e.markers ?? []) compiles(`entity ${name} marker ${k.label}`, k.pattern);
    if (e.nameSuffix) compiles(`entity ${name} nameSuffix`, e.nameSuffix);
    for (const c of e.codes ?? []) {
      for (const s of c.shapes) if (!e.shapes.includes(s)) problems.push(`entity ${name} code ${c.label}: ${s} isn't one of its shapes`);
    }
  }
  for (const i of m.intents) {
    for (const p of i.params) {
      if (p.kind === "text" && p.pattern) compiles(`${i.id}.${p.name}`, p.pattern);
      if (p.format) compiles(`${i.id}.${p.name} format`, p.format);
      if (p.kind === "entity" && !m.entities[p.entity]) problems.push(`${i.id}.${p.name}: no entity named ${p.entity}`);
    }
    const pair = i.orderedPair;
    if (pair) {
      const names = i.params.map((p) => p.name);
      if (pair.first === pair.second || !names.includes(pair.first) || !names.includes(pair.second)) {
        problems.push(`${i.id}: orderedPair must name two of its parameters`);
      }
      if (pair.cue) compiles(`${i.id} orderedPair cue`, pair.cue);
    }
    const labels = i.params.map((p) => p.label);
    if (new Set(labels).size !== labels.length) problems.push(`${i.id}: parameter labels must be unique, for span extractors`);
  }
  for (const g of m.golden ?? []) {
    if (g.expectIntent !== null && !m.intents.some((i) => i.id === g.expectIntent)) problems.push(`golden "${g.question}": no intent named ${g.expectIntent}`);
  }
  return problems;
}

/**
 * Decodes a hub manifest that checkManifestIntegrity passes: what a reader
 * that uses a manifest decodes, such as the answer path, so none has to
 * remember to check it. Each of its problems is one issue. The build and
 * staging also call checkManifest.
 * HubManifest decodes one without the checks, to show a toolset whatever
 * its state.
 */
export const ValidHubManifest = HubManifest.check(Schema.makeFilter(checkManifestIntegrity));

const decodeValidManifestJson = Schema.decodeUnknownResult(Schema.fromJsonString(ValidHubManifest));
/**
 * A manifest as JSON text, decoded with ValidHubManifest: the manifest, or
 * why it can't be used, on one line, as parseManifestJson says it.
 */
export const parseValidManifestJson = (text: string): Result.Result<HubManifest, string> => Result.mapError(decodeValidManifestJson(text), oneLine);
